import { useContext, useEffect, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import * as uuid from "uuid";
import Loader from "../../../components/Loader";
import {
  TEST_LOAD_SUCCESS,
  UPDATE_CURRENT_SESSION_TEST_DATA,
} from "../../../store/actions";
import { Context } from "../../../store/store";
import { API_URLS } from "../../../utils/api-constants";
import { setFavIcon, tryEmbedCssLink } from "../../../utils/embed-utils";
import { axiosGet } from "../../../utils/queries";
import { isObjectId } from "../../../utils/regex";
import {
  ATTRIBUTES_SUB_URL,
  CUSTOM_RESULTS_SUB_URL,
  HASH_TYPE,
  PUBLIC_SITE_URL,
  RESULTS_SUB_URL,
  SPECIAL_ATTRIBUTES_SUB_URL,
} from "../constants";
import {
  TestAttributes,
  TestInfo,
  TestResults,
  TestSet,
  TestSpecialAttributes,
  CustomTestResults,
} from "./sub-pages";
import { buildUrlWithGetParams, getTestUrlForSession } from "./utils";
import { useAttrsSessionId } from "./hooks/useAttrsSessionId";

function Test() {
  const navigate = useNavigate();
  const [state, dispatch] = useContext(Context);
  const { testSessionData, publicTest } = state;
  const { testId, "*": splat } = useParams();
  // In React Router v6, child route params (e.g. sessionId from results/:sessionId)
  // are not visible in the parent component's useParams(). Extract sessionId from
  // the wildcard splat so direct links like /test/:testId/results/:sessionId still work.
  const splatSessionId = splat?.match(/(?:cresults|results)\/([^/?]+)/)?.[1];
  const attrsSessionId = useAttrsSessionId();
  const sessionId =
    attrsSessionId ?? splatSessionId ?? testSessionData.sessionId;

  const [isLoading, setIsLoading] = useState(true);
  const { cssLink: testCssUrl, client, group, name: testName } = publicTest;

  useEffect(() => {
    if (testId && isObjectId(testId)) {
      let url = `${API_URLS.PUBLIC_TEST}/${testId}`;

      if (uuid.validate(sessionId)) {
        url = `${API_URLS.PUBLIC_TEST}/${testId}/${sessionId}`;
      }

      axiosGet(buildUrlWithGetParams(url))
        .then(payload => {
          dispatch({ type: TEST_LOAD_SUCCESS, payload });
          setIsLoading(false);
          navigate(getTestUrlForSession(payload, sessionId));
        })
        .catch(() => {
          navigate("/");
        });
    } else {
      document.location.href = PUBLIC_SITE_URL;
    }
  }, [testId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cssLink = null;

    if (group?.cssLink) {
      cssLink = tryEmbedCssLink(group.cssLink);
    } else if (testCssUrl) {
      cssLink = tryEmbedCssLink(testCssUrl);
    } else if (client?.cssLink) {
      cssLink = tryEmbedCssLink(client.cssLink);
    }

    return () => {
      if (cssLink) {
        cssLink.remove();
      }
    };
  }, [testCssUrl, client, group]);

  useEffect(() => {
    if (testName) {
      document.title = testName;
    }

    if (client?.favIconUrl) {
      setFavIcon(client.favIconUrl);
    }
  }, [testName, client]);

  useEffect(() => {
    const queryString = window.location.search;
    const searchParams = new URLSearchParams(queryString);
    const isManager = searchParams.has("aKey")
      ? searchParams.get("aKey") === HASH_TYPE.MANAGER
      : undefined;

    dispatch({
      type: UPDATE_CURRENT_SESSION_TEST_DATA,
      payload: { isManager, sessionId },
    });
  }, [dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Routes>
      <Route index element={<TestInfo />} />
      <Route
        path={SPECIAL_ATTRIBUTES_SUB_URL}
        element={<TestSpecialAttributes />}
      />
      <Route path={ATTRIBUTES_SUB_URL} element={<TestAttributes />} />
      <Route path={`${RESULTS_SUB_URL}/:sessionId`} element={<TestResults />} />
      <Route
        path={`${CUSTOM_RESULTS_SUB_URL}/:sessionId`}
        element={<CustomTestResults />}
      />
      <Route path=":testSetId" element={<TestSet />} />
    </Routes>
  );
}

export default Test;
